import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { fetchSupplierPreferences, upsertSupplierPreference, deleteSupplierPreference } from '../services/api'
import { useAuth } from '../composables/useAuth'
import { trackEvent } from '../services/analytics'

interface PreferenceEntry {
    isFavorite: boolean
    isBlocked: boolean
    isDirectContract: boolean
    supplierName: string
    supplierSlug: string
    supplierLogoUrl: string | null
}

export const useSupplierPreferencesStore = defineStore('supplierPreferences', () => {
    const preferences = ref<Map<string, PreferenceEntry>>(new Map())
    const loaded = ref(false)
    const loading = ref(false)

    const { getAccessTokenSilently } = useAuth()

    // Getters
    const isFavorite = (supplierId: string) => preferences.value.get(supplierId)?.isFavorite ?? false
    const isBlocked = (supplierId: string) => preferences.value.get(supplierId)?.isBlocked ?? false
    const isDirectContract = (supplierId: string) => preferences.value.get(supplierId)?.isDirectContract ?? false

    const favorites = computed(() => {
        const result: Array<{ supplierId: string } & PreferenceEntry> = []
        preferences.value.forEach((pref, supplierId) => {
            if (pref.isFavorite) result.push({ supplierId, ...pref })
        })
        return result
    })

    const blocked = computed(() => {
        const result: Array<{ supplierId: string } & PreferenceEntry> = []
        preferences.value.forEach((pref, supplierId) => {
            if (pref.isBlocked) result.push({ supplierId, ...pref })
        })
        return result
    })

    const directContracts = computed(() => {
        const result: Array<{ supplierId: string } & PreferenceEntry> = []
        preferences.value.forEach((pref, supplierId) => {
            if (pref.isDirectContract) result.push({ supplierId, ...pref })
        })
        return result
    })

    // Actions
    async function load() {
        if (loading.value) return
        loading.value = true
        try {
            const token = await getAccessTokenSilently()
            const data = await fetchSupplierPreferences(token)
            const newMap = new Map<string, PreferenceEntry>()
            // API returns snake_case fields
            for (const p of data.preferences) {
                newMap.set(p.supplier_id, {
                    isFavorite: p.is_favorite,
                    isBlocked: p.is_blocked,
                    isDirectContract: p.is_direct_contract,
                    supplierName: p.supplier_name,
                    supplierSlug: p.supplier_slug,
                    supplierLogoUrl: p.supplier_logo_url
                })
            }
            preferences.value = newMap
            loaded.value = true
        } catch (e) {
            console.error('Failed to load supplier preferences:', e)
        } finally {
            loading.value = false
        }
    }

    async function _upsert(supplierId: string, data: { is_favorite?: boolean; is_blocked?: boolean; is_direct_contract?: boolean }) {
        const token = await getAccessTokenSilently()
        const result = await upsertSupplierPreference(supplierId, data, token)
        const pref = result.preference
        preferences.value.set(supplierId, {
            isFavorite: pref.is_favorite,
            isBlocked: pref.is_blocked,
            isDirectContract: pref.is_direct_contract,
            supplierName: pref.supplier_name,
            supplierSlug: pref.supplier_slug,
            supplierLogoUrl: pref.supplier_logo_url
        })
        // Force reactivity
        preferences.value = new Map(preferences.value)
    }

    async function toggleFavorite(supplierId: string) {
        const current = isFavorite(supplierId)
        const newValue = !current
        // Optimistic update
        const existing = preferences.value.get(supplierId)
        if (existing) {
            existing.isFavorite = newValue
            preferences.value = new Map(preferences.value)
        }

        try {
            await _upsert(supplierId, { is_favorite: newValue })
            trackEvent('supplier_preference_toggled', {
                supplier_id: supplierId,
                preference_type: 'favorite',
                enabled: newValue
            })
        } catch (e) {
            // Revert on failure
            if (existing) {
                existing.isFavorite = current
                preferences.value = new Map(preferences.value)
            }
            console.error('Failed to toggle favorite:', e)
        }

        // Clean up if all flags are false
        await _cleanupIfEmpty(supplierId)
    }

    async function toggleBlocked(supplierId: string) {
        const current = isBlocked(supplierId)
        const newValue = !current
        const existing = preferences.value.get(supplierId)
        if (existing) {
            existing.isBlocked = newValue
            preferences.value = new Map(preferences.value)
        }

        try {
            await _upsert(supplierId, { is_blocked: newValue })
            trackEvent('supplier_preference_toggled', {
                supplier_id: supplierId,
                preference_type: 'blocked',
                enabled: newValue
            })
        } catch (e) {
            if (existing) {
                existing.isBlocked = current
                preferences.value = new Map(preferences.value)
            }
            console.error('Failed to toggle blocked:', e)
        }

        await _cleanupIfEmpty(supplierId)
    }

    async function toggleDirectContract(supplierId: string) {
        const current = isDirectContract(supplierId)
        const newValue = !current
        const existing = preferences.value.get(supplierId)
        if (existing) {
            existing.isDirectContract = newValue
            preferences.value = new Map(preferences.value)
        }

        try {
            await _upsert(supplierId, { is_direct_contract: newValue })
            trackEvent('supplier_preference_toggled', {
                supplier_id: supplierId,
                preference_type: 'direct_contract',
                enabled: newValue
            })
        } catch (e) {
            if (existing) {
                existing.isDirectContract = current
                preferences.value = new Map(preferences.value)
            }
            console.error('Failed to toggle direct contract:', e)
        }

        await _cleanupIfEmpty(supplierId)
    }

    async function _cleanupIfEmpty(supplierId: string) {
        const pref = preferences.value.get(supplierId)
        if (pref && !pref.isFavorite && !pref.isBlocked && !pref.isDirectContract) {
            try {
                const token = await getAccessTokenSilently()
                await deleteSupplierPreference(supplierId, token)
                preferences.value.delete(supplierId)
                preferences.value = new Map(preferences.value)
            } catch {
                // Non-critical, preference row with all false is fine
            }
        }
    }

    async function removePreference(supplierId: string) {
        try {
            const token = await getAccessTokenSilently()
            await deleteSupplierPreference(supplierId, token)
            preferences.value.delete(supplierId)
            preferences.value = new Map(preferences.value)
        } catch (e) {
            console.error('Failed to remove preference:', e)
        }
    }

    return {
        preferences,
        loaded,
        loading,
        isFavorite,
        isBlocked,
        isDirectContract,
        favorites,
        blocked,
        directContracts,
        load,
        toggleFavorite,
        toggleBlocked,
        toggleDirectContract,
        removePreference
    }
})
